/**
 * One report template: its current definition and versions, the version being
 * looked at (in the URL, current by default), and that version's sample-data
 * PDF, printed by the orchestrator and handed to the browser's own viewer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { agentBaseUrl } from '../../lib/agentApi.js';
import { apiOrigin, apiRequest, ApiError, getAuthToken } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

const sectionSchema = z
  .object({
    kind: z.string(),
    title: z.string(),
    display: z.string().optional(),
    sections: z.array(z.object({ kind: z.string(), title: z.string() }).passthrough()).optional(),
  })
  .passthrough();

const definitionSchema = z
  .object({
    units: z
      .object({
        kind: z.enum(['filters', 'unit_list']),
        billingStatus: z.string().nullable(),
        fleetStatus: z.string().nullable(),
        unitComponent: z.string().nullable(),
      })
      .passthrough(),
    period: z.enum(['month', 'date_range']),
    sections: z.array(sectionSchema),
  })
  .passthrough();

const detailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    owner: z.object({ id: z.string(), name: z.string().nullable() }),
    visibility: z.enum(['private', 'tenant']),
    access: z
      .object({ copy: z.boolean(), version: z.boolean(), manage: z.boolean() })
      .passthrough(),
    version: z.number(),
    template: definitionSchema,
    versions: z.array(
      z
        .object({
          number: z.number(),
          createdById: z.string(),
          createdAt: z.string(),
          restoredFrom: z.number().nullable().optional(),
        })
        .passthrough(),
    ),
    shares: z.array(z.object({ name: z.string().nullable(), email: z.string() }).passthrough()),
  })
  .passthrough();

const versionSchema = z.object({ number: z.number(), template: definitionSchema }).passthrough();

const runSchema = z
  .object({
    id: z.string(),
    templateVersion: z.number(),
    values: z.object({
      customer: z.string().nullable(),
      region: z.string().nullable(),
      units: z.string(),
      period: z.string(),
    }),
    status: z.enum(['queued', 'running', 'done', 'failed']),
    error: z.string().nullable(),
    createdAt: z.string(),
  })
  .passthrough();

export const TEMPLATE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'runs', label: 'Your runs' },
  { id: 'schedules', label: 'Schedules' },
] as const;
export type TemplateTab = (typeof TEMPLATE_TABS)[number]['id'];

export type ReportTemplateDetail = z.infer<typeof detailSchema>;
export type ReportDefinition = z.infer<typeof definitionSchema>;
export type ReportSection = z.infer<typeof sectionSchema>;
export type ReportRun = z.infer<typeof runSchema>;

/** Save one of the caller's finished runs; the API checks it is theirs and streams it from storage. */
export const downloadRunPdf = async (runId: string): Promise<void> => {
  const token = await getAuthToken();
  const response = await fetch(`${apiOrigin}/api/report-runs/${runId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new ApiError(response.status, 'The PDF could not be downloaded.');
  const name =
    /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ??
    `report-${runId.slice(0, 8)}.pdf`;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

const fetchPreview = async (id: string, number: number): Promise<string> => {
  const token = await getAuthToken();
  const response = await fetch(
    `${agentBaseUrl}/api/report-templates/${id}/versions/${String(number)}/preview`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) throw new ApiError(response.status, 'The preview could not be printed.');
  return URL.createObjectURL(await response.blob());
};

/** `canSchedule`: the Schedules tab is Reports.Write's, like scheduling in chat. */
export const useReportTemplatePageLogic = (
  id: string,
  { canSchedule }: { canSchedule: boolean },
) => {
  const template = useQuery({
    queryKey: queryKeys.reportTemplate(id),
    queryFn: async () =>
      detailSchema.parse(await apiRequest<unknown>(`/api/report-templates/${id}`)),
  });

  const [params, setParams] = useSearchParams();
  const tabs = TEMPLATE_TABS.filter((t) => t.id !== 'schedules' || canSchedule);
  const tab = tabs.find((t) => t.id === params.get('tab'))?.id ?? 'overview';
  const setTab = (next: TemplateTab) =>
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === 'overview') nextParams.delete('tab');
        else nextParams.set('tab', next);
        return nextParams;
      },
      { replace: true },
    );
  const current = template.data?.version;
  const asked = Number(params.get('version'));
  const known = template.data?.versions.some((v) => v.number === asked) ?? false;
  const selected = known ? asked : current;
  const isCurrent = selected === current;
  const selectVersion = (number: number) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (number === current) next.delete('version');
        else next.set('version', String(number));
        return next;
      },
      { replace: true },
    );

  // An older version's definition is its own read; the current one comes with the template.
  const older = useQuery({
    queryKey: queryKeys.reportTemplateVersion(id, selected ?? 0),
    queryFn: async () =>
      versionSchema.parse(
        await apiRequest<unknown>(`/api/report-templates/${id}/versions/${String(selected)}`),
      ),
    enabled: selected !== undefined && !isCurrent,
  });

  // Restore saves the shown version as the next one; then show the report as it now is.
  const queryClient = useQueryClient();
  const restore = useMutation({
    mutationFn: (number: number) =>
      apiRequest<unknown>(`/api/report-templates/${id}/versions/${String(number)}/restore`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplate(id) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplates() }),
      ]);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('version');
          return next;
        },
        { replace: true },
      );
    },
  });

  // Only the caller's own runs of this template; other people's stay private to them.
  const runs = useQuery({
    queryKey: queryKeys.reportTemplateRuns(id),
    queryFn: async () =>
      z
        .array(runSchema)
        .parse(await apiRequest<unknown>(`/api/report-runs?templateId=${id}&limit=100`)),
    enabled: tab === 'runs',
  });

  const preview = useQuery({
    queryKey: queryKeys.reportPreview(id, selected ?? 0),
    queryFn: () => fetchPreview(id, selected ?? 0),
    enabled: selected !== undefined && tab === 'overview',
    staleTime: Infinity,
    gcTime: 0,
  });

  return {
    template,
    tabs,
    tab,
    setTab,
    runs,
    selected,
    isCurrent,
    selectVersion,
    restore,
    definition: isCurrent ? template.data?.template : older.data?.template,
    definitionLoading: !isCurrent && older.isLoading,
    preview,
  };
};
